import React, { useContext, useEffect, useState } from 'react';
import { labels, SELECT, TRAFFIC_DISTRIBUTION } from '../../utils/Constants';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { ABContext } from '../../contexts/ABContext';
import { getLiveApplets, getModelsBasedOnChannelKey } from '../../services/ABServices';
import { TModel, TModelData } from '../../types/ab';
import { AppContext } from 'Context/AppContext';
import { CoSellerModelIcon, SubstitutionModelIcon } from 'assests/GmsIcons';
import Button from 'Components/DesignSystem/Button/Button';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Slider from 'Components/DesignSystem/Slider/Slider';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { DropdownOption } from 'types/DesignSystem/Dropdown';
import ConfirmDialog from './ConfirmDialog';

/*
  The three columns of a row have to line up, and hand-matching spacer heights to
  `Dropdown`'s label row is how they drift apart the moment either changes.

  So the caption IS a `FieldTitle` — the same component `Dropdown` renders internally,
  at the same `size`, with the same `tw-mb-2` — and the columns that have no caption
  render that same element `invisible`. Same markup, same height, by construction
  rather than by measurement.
*/
const LabelRow = ({ label }: { label?: string }) => (
    <FieldTitle
        label={label ?? '\u00A0'}
        size="sm"
        className={`tw-mb-2${label ? '' : ' tw-invisible'}`}
        aria-hidden={label ? undefined : true}
        data-testid={label ? 'detail-traffic-distribution-caption' : undefined}
    />
);

/*
  Every control in a row sits in a box of the shared `md` control height (36px), so
  their CENTRES align — a 20px slider thumb and a 36px select do not otherwise, and
  `items-start` left the slider riding ~8px high above both its neighbours.
*/
const CONTROL_ROW = 'tw-h-9 tw-flex tw-items-center';

const labelNames = [
    'Model A (Control)',
    'Model B (Variant)',
    'Model C (Variant)',
    'Model D (Variant)',
];

const TrafficDistributionSection = () => {
    const abContext = useContext(ABContext);
    const appContext = useContext(AppContext);
    const { defaultModels, models, setModels, modelData, setModelData, liveApplets, setLiveApplets, setDefaultModels } = useContext(ABContext);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [dialogDisplayed, setDialogDisplayed] = useState<boolean>(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

    useEffect(() => {
        if (abContext?.selectedApplet) {
            getModels();
            setDialogDisplayed(false)
        }
    }, [abContext?.selectedApplet]);

    useEffect(() => {
        const applets = abContext?.payload?.applets;

        if (applets && applets.length > 0) {
            const firstApplet = applets[0];
            const firstAppletId = firstApplet?.applet_id;
            abContext?.setSelectedApplet(firstAppletId);

            const userSavedModels = applets[0]?.models;

            if (userSavedModels?.length > 0) {
                processAndSetModels(userSavedModels);
            } else {
                selectDefaultModel(modelData, firstAppletId);
            }
        }
    }, [abContext?.payload?.applets]);

    const processAndSetModels = async (modelsToProcess: TModel[]) => {
        const freshModelData = await getModels();

        if (freshModelData) {

            const processedModels = modelsToProcess.map((model) => {
                let modelType = model.type;

                if (!modelType) {
                    const matchedModel = freshModelData.find((m: TModel) => m.model_id === model.model_id);
                    if (matchedModel && matchedModel.type) {
                        modelType = matchedModel.type;
                    }
                }

                return {
                    label: model.label,
                    is_control: model.is_control,
                    model_id: model.model_id,
                    model_name: model.model_name,
                    traffic_distribution: model.traffic_distribution,
                    type: modelType
                };
            });

            setModels(processedModels);
        } else {
            setModels(modelsToProcess);
        }
    };


    const showConfirmDialog = (onConfirm: () => void) => {
        setPendingAction(() => onConfirm);
        setShowDialog(true);
    };

    const handleConfirm = () => {
        setDialogDisplayed(true)
        if (pendingAction) pendingAction();
        setShowDialog(false);
    };

    const handleCancel = () => {
        setShowDialog(false);
    };


    const getModels = async () => {
        try {
            if (!appContext?.channelKey) return
            const response = await getModelsBasedOnChannelKey(appContext?.channelKey);
            if (response?.done) {
                setModelData(response?.data);
                return response?.data;
            }
            return null;
        } catch (error) {
            console.error("getModels ~ error:", error);
            return null;
        }
    }

    const fetchLiveAppletsAndDefaultModels = async () => {
        if (!appContext?.channelKey) return
        try {
            const liveAppletsResponse = await getLiveApplets(appContext?.channelKey)
            setLiveApplets(liveAppletsResponse?.data?.ab_test_applet || []);
            setDefaultModels(liveAppletsResponse?.data?.default_models || {})
            return liveAppletsResponse?.data?.default_models;
        } catch (error) {
            setLiveApplets([]);
            setDefaultModels({});
            toast.error('Failed to fetch live applets');
        }
    }

    const selectDefaultModel = async (inputData: TModelData[] | undefined, selectedAppletId: string) => {

        let modelDataToUse = inputData;
        if (!modelDataToUse || modelDataToUse.length === 0) {
            try {
                if (!appContext?.channelKey) {
                    return;
                }

                const response = await getModelsBasedOnChannelKey(appContext.channelKey);

                if (!response?.done || !response?.data || response?.data.length === 0) {
                    console.error("Failed to fetch models or no models returned");
                    return;
                }

                setModelData(response.data);
                modelDataToUse = response.data;
            } catch (error) {
                console.error("Error fetching models:", error);
                return;
            }
        }

        let currentDefaultModels = defaultModels;
        const hasDefaultModelForApplet = currentDefaultModels &&
            Object.keys(currentDefaultModels).length > 0 &&
            currentDefaultModels[selectedAppletId] !== undefined;

        if (!hasDefaultModelForApplet) {
            currentDefaultModels = await fetchLiveAppletsAndDefaultModels();

            if (!currentDefaultModels || !currentDefaultModels[selectedAppletId]) {
                return;
            }
        }

        const defaultModelIdForSelectedApplet = currentDefaultModels[selectedAppletId];

        const defaultModel: TModelData | undefined = modelDataToUse?.find(
            (e: TModelData) => e?.model_id === defaultModelIdForSelectedApplet
        );

        if (!defaultModel) {
            return;
        }

        setModels([{
            label: labels[0],
            traffic_distribution: 100,
            model_id: defaultModel.model_id,
            is_control: true,
            model_name: defaultModel.model_name,
            type: defaultModel.type
        }]);

        return defaultModel;
    };

    const removeModel = (modelId: string | null) => {
        setModels((prevModels: TModel[]) => {
            if (prevModels.length === 1) {
                toast.warning("At least one model should be present.");
                return prevModels;
            }
            const updatedModels = prevModels.filter((model, index) => {
                if (modelId === null) {
                    return index !== prevModels.length - 1;
                }
                return model.model_id !== modelId;
            });

            const equalPercentage = parseFloat((100 / updatedModels.length).toFixed(1));
            let totalPercentage = equalPercentage * (updatedModels.length - 1);
            const lastModelPercentage = parseFloat((100 - totalPercentage).toFixed(1));

            return updatedModels.map((model, index) => ({
                ...model,
                traffic_distribution: index < updatedModels.length - 1 ? equalPercentage : lastModelPercentage
            }));
        });
    };

    const addModel = () => {
        if (models.length < 4) {
            const newModel: TModel = { label: labels[models.length], traffic_distribution: 0, model_id: '', is_control: false, model_name: "" };
            setModels((prevModels: TModel[]) => {
                const updatedModels = [...prevModels, newModel];
                const equalPercentage = parseFloat((100 / updatedModels.length).toFixed(1));
                let totalPercentage = equalPercentage * (updatedModels.length - 1);
                const lastModelPercentage = parseFloat((100 - totalPercentage).toFixed(1));

                return updatedModels.map((model, index) => ({
                    ...model,
                    traffic_distribution: index < updatedModels.length - 1 ? equalPercentage : lastModelPercentage
                }));
            });
        } else {
            toast.warning("You've reached the model limit.");
        }
    };

    const handleSliderChange = (index: number, value: number) => {
        setModels((prevModels: TModel[]) => {
            const totalPercentage = prevModels.reduce((acc, model) => acc + model.traffic_distribution, 0);
            const remainingPercentage = totalPercentage - prevModels[index].traffic_distribution;

            let newModels = prevModels.map((model, i) => {
                if (i === index) {
                    return { ...model, traffic_distribution: value };
                }
                return model;
            });

            if (remainingPercentage > 0) {
                const scaleFactor = (100 - value) / remainingPercentage;
                newModels = newModels.map((model, i) => {
                    if (i !== index) {
                        return { ...model, traffic_distribution: model.traffic_distribution * scaleFactor };
                    }
                    return model;
                });
            } else if (value < 100) {
                const otherModelsCount = prevModels.length - 1;
                const equalPercentage = (100 - value) / otherModelsCount;
                newModels = newModels.map((model, i) => {
                    if (i !== index) {
                        return { ...model, traffic_distribution: equalPercentage };
                    }
                    return model;
                });
            }

            return newModels.map(model => ({
                ...model,
                traffic_distribution: parseFloat(model.traffic_distribution.toFixed(1))
            }));
        });
    };

    const handleModelSelect = (index: number, modelName: string, selectedModelId: string, type?: "Co-seller" | "Substitution") => {
        if (index === 0 && models?.length > 0 && models[0]?.model_name && !dialogDisplayed && defaultModels[abContext?.selectedApplet]) {
            showConfirmDialog(() => {
                setModels((prevModels: TModel[]) =>
                    prevModels.map((model, i) =>
                        i === index ? { ...model, model_name: modelName, type: type, model_id: selectedModelId, traffic_distribution: index === 0 && prevModels?.length === 0 ? 100 : model?.traffic_distribution } : model
                    )
                );
            });
            return;
        }
        setModels((prevModels: TModel[]) =>
            prevModels.map((model, i) =>
                i === index ? { ...model, model_name: modelName, type: type, model_id: selectedModelId, traffic_distribution: index === 0 && prevModels?.length === 0 ? 100 : model?.traffic_distribution } : model
            )
        );
    };

    const getAvailableModels = (currentIndex: number) => {
        const selectedModelIds = models.map((model: TModel) => model.model_id);
        return modelData.filter((model: TModel) =>
            !selectedModelIds.includes(model.model_id.toString()) ||
            model.model_id.toString() === models[currentIndex].model_id
        );
    };

    const hasAvailableModelsToSelect = () => {
        const selectedModelIds = models.map((model: TModel) => model.model_id);
        return modelData.some((model: TModel) => !selectedModelIds.includes(model.model_id.toString()));
    };
    
    /*
      One option list, built once per row, and the single source for BOTH the
      Dropdown and the lookup its `onChange` does.

      `react-select` handed `handleModelSelect` the whole option object — label,
      value and type together. The design system's `Dropdown` hands back the VALUE
      only (a deliberate narrowing: an option object in a change handler is how
      callers end up depending on internal option shape). Looking the option back up
      here keeps `handleModelSelect`'s four-argument signature, and the redistribution
      and control-model-change logic behind it, byte-identical.
    */
    const optionsFor = (index: number): DropdownOption[] => ([
        { value: '', label: SELECT },
        ...getAvailableModels(index).map((availableModel: TModel) => ({
            value: availableModel.model_id,
            label: availableModel.model_name,
            /* Decorative — `DropdownOption.icon` is `aria-hidden` by construction.
               Same two glyphs, same `type` test, as the `formatOptionLabel` this
               replaces; the empty "Select" row carried none and still does. */
            icon: availableModel.type === 'Co-seller' ? <CoSellerModelIcon />
                : availableModel.type === 'Substitution' ? <SubstitutionModelIcon />
                : undefined,
        })),
    ]);

    return (<>
        <section className='tw-pt-2 tw-pb-6 tw-border-b'>
            {models?.map((model: TModel, index: number) => (
                <div key={model.label} className='tw-flex tw-items-start tw-gap-8 tw-py-2'>
                    <div className="tw-w-[20%]">
                        {/*
                          The label is the Dropdown's own now, not a bare `<label>`
                          floating above a `react-select` with no `htmlFor`. Four
                          model pickers stacked in a column, and not one of them was
                          associated with the text naming it.
                        */}
                        <Dropdown
                            label={labelNames[index]}
                            labelSize="sm"
                            placeholder={SELECT}
                            data-testid={`detail-traffic-model-${index}`}
                            value={model?.model_id ?? ''}
                            options={optionsFor(index)}
                            onChange={(selectedValue) => {
                                const chosen = optionsFor(index).find((option) => option.value === selectedValue);
                                if (!chosen) return;
                                const source = modelData.find(
                                    (candidate: TModel) => candidate.model_id === selectedValue,
                                );
                                handleModelSelect(
                                    index,
                                    chosen.label,
                                    String(selectedValue),
                                    source?.type,
                                );
                            }}
                        />
                    </div>
                    <div className="tw-w-[20%]">
                        {/*
                          The caption renders once, over row 0 — which is what the
                          legacy markup did too, except it emitted an EMPTY `<label>`
                          for rows 1–3 rather than nothing. Rows 1–3 get the same
                          element, invisible, so the three columns stay aligned; the
                          name each slider needs is on the slider, via `hideLabel`.
                        */}
                        <LabelRow label={index === 0 ? TRAFFIC_DISTRIBUTION : undefined} />
                        <div className={CONTROL_ROW}>
                            <Slider
                                /*
                                  `min={0}`, stated rather than defaulted. The model
                                  value reaches exactly 0 whenever another model is
                                  dragged to 100% — `handleSliderChange`'s scaleFactor
                                  goes to zero — and the old `min` of 1 made the input
                                  report and announce "1" while the % box beside it
                                  read "0%".

                                  `step={1}` is also deliberate. The redistribution
                                  emits one-decimal values (three models → 33.3 /
                                  33.3 / 33.4) which the range input snaps to the
                                  integer grid, so the thumb can sit 0.3% of the track
                                  from the readout. A fractional step would close that
                                  and BREAK THE KEYBOARD: the call site floors every
                                  emitted value, so a 0.1 arrow-key nudge floors back
                                  to where it started and the arrows do nothing.
                                */
                                label={`Traffic distribution — ${labelNames[index]}`}
                                hideLabel
                                /* The slider's own root is a flex COLUMN; as a flex item
                                   inside `CONTROL_ROW` it would shrink to its content
                                   and the track would collapse. */
                                className="tw-w-full"
                                min={0}
                                max={100}
                                step={1}
                                formatValue={(percentage) => `${percentage}%`}
                                data-testid={`detail-traffic-slider-${index}`}
                                disabled={false}
                                value={model?.traffic_distribution || 0}
                                onChange={(percentage) => handleSliderChange(index, Math.floor(percentage))}
                            />
                        </div>
                    </div>
                    <div className="tw-w-[8%]">
                        <LabelRow />
                        {/*
                          A read-only mirror of the slider, not a field.

                          It was `<input readOnly>`: a tab stop that announces as an
                          editable text box, holding a value the control beside it
                          already announces through `aria-valuetext`. As static text
                          it is one fewer stop and one fewer duplicate announcement;
                          `aria-hidden` because the slider is the accessible source.
                        */}
                        <div
                            aria-hidden="true"
                            className={`${CONTROL_ROW} tw-px-3 tw-border tw-border-edge tw-rounded-ctl tw-bg-surface`}
                            data-testid={`detail-traffic-percent-${index}`}
                        >
                            <Typography variant="body-primary" as="span" className="tw-text-ink">
                                {model ? `${model.traffic_distribution}%` : '0%'}
                            </Typography>
                        </div>
                    </div>
                    {index !== 0 && (
                        <div>
                            <LabelRow />
                            <div className={CONTROL_ROW}>
                            <Button
                                variant="danger"
                                size="sm"
                                data-testid={`detail-traffic-remove-${index}`}
                                onClick={() => removeModel(model?.model_id)}
                            >
                                Remove
                            </Button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
            {models.length < 4 && models[models.length - 1]?.model_id && hasAvailableModelsToSelect() && (
                <div className="tw-mt-3">
                    <Button
                        variant="tertiary"
                        size="sm"
                        data-testid="detail-traffic-add-model"
                        onClick={addModel}
                    >
                        + Models
                    </Button>
                </div>
            )}
        </section>
        {showDialog && <ConfirmDialog
            title={liveApplets && liveApplets?.length > 0 && liveApplets?.includes(abContext?.selectedApplet) ? 'Control Model Change' : 'Change in Promoted Model'}
            message={liveApplets && liveApplets?.length > 0 && liveApplets?.includes(abContext?.selectedApplet) ?
                `"${models[0].model_name}" is currently the control model in an existing AB test. Do you want to change it?`
                : `"${models[0].model_name}" is currently promoted with 100% traffic in Commend. Selecting a different control model will stop ${models[0].model_name}. Continue?`
            }
            onConfirm={handleConfirm}
            onClose={handleCancel}
            confirmText="Yes, Continue"
            cancelText="Cancel"
        />}
    </>
    );

}

export default TrafficDistributionSection;